import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

/**
 * Accept a team invitation.
 *
 * Two ways to arrive here:
 *
 *  - From `/auth/confirm`, having just been signed in by the API's
 *    `/join/accept` route. The one-click path.
 *  - Directly, when the link was not validly signed. The API forwards here
 *    without minting a session, so the sign-in screen appears first and the
 *    email on the resulting session is what authorizes acceptance.
 *
 * Either way the token alone never grants membership: the API compares the
 * invitation's address against the session's own verified email.
 */

/** Codes the API redirects with. Mapped here so no server text reaches the page. */
const JOIN_ERRORS: Record<string, string> = {
  'invalid-link': 'That invitation link is malformed. Ask for a new invitation.',
  'not-found': 'That invitation no longer exists. It may have already been accepted or revoked.',
  expired: 'That invitation has expired. Ask whoever invited you to send a new one.',
  unknown: 'Something went wrong opening that invitation. Try the link again.',
};

const ACCEPT_ERRORS: Record<string, string> = {
  invitation_not_found: 'That invitation no longer exists. It may have been revoked.',
  invitation_expired: 'That invitation has expired. Ask for a new one.',
  invitation_email_mismatch:
    'This invitation was sent to a different email address. Sign in as that address to accept it.',
};

export const Route = createFileRoute('/join')({
  validateSearch: (search: Record<string, unknown>) => ({
    invite_token: typeof search.invite_token === 'string' ? search.invite_token : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  component: JoinPage,
});

function JoinPage() {
  const { invite_token: inviteToken, error: errorCode } = Route.useSearch();
  const { session, setActiveAccountId } = useAuth();
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(errorCode ? codeToMessage(errorCode) : null);

  /**
   * Accepting is not idempotent — `accept_invitation` deletes the invitation
   * row — so StrictMode's double effect would run the second call against a
   * token that no longer exists and show a failure on a successful join.
   */
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current || errorCode || !inviteToken || !session) return;
    attempted.current = true;

    adminApi.team
      .accept(inviteToken)
      .then(async ({ accountId }) => {
        /**
         * The claims in the current token were minted before this membership
         * existed, so `account_ids` does not contain it and every RLS policy
         * would deny — the console would render an empty workspace. Forcing a
         * refresh re-runs `custom_access_token_hook` and mints a token that
         * knows about the new account.
         */
        await supabase.auth.refreshSession();

        setActiveAccountId(accountId);
        navigate({ to: '/', replace: true });
      })
      .catch((cause: Error) => setError(codeToMessage(cause.message)));
  }, [inviteToken, errorCode, session, navigate, setActiveAccountId]);

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 text-center">
        {error ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight">Cannot accept this invitation</h1>
            <p className="text-muted-foreground text-sm">{error}</p>
            <Button onClick={() => navigate({ to: '/', replace: true })}>Go to the console</Button>
          </>
        ) : !inviteToken ? (
          <>
            <h1 className="text-xl font-semibold tracking-tight">No invitation</h1>
            <p className="text-muted-foreground text-sm">
              This page needs an invitation link. Open the one you were emailed.
            </p>
            <Button onClick={() => navigate({ to: '/', replace: true })}>Go to the console</Button>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">Joining the workspace…</p>
        )}
      </div>
    </div>
  );
}

/** Falls through to the raw text for anything not in the tables. */
function codeToMessage(code: string): string {
  return JOIN_ERRORS[code] ?? ACCEPT_ERRORS[code] ?? code;
}
